import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { carryOverRebuilding, hasRebuildingCells, REBUILDING_POLL_MS } from '@/api/rebuildingCells';
import { applyBirdEyeStatusToCell } from '../adapters/birdEyeAdapters';
import { birdEyeApi } from '../api/birdEyeApi';
import type { BirdEyeCellRaw, BirdEyeResponse, BirdEyeVisitStatus } from '../api/types';

/** Merges a status change into one cached board cell; every other field is left as is. */
export function patchBirdEyeCell(
  queryClient: QueryClient,
  locationId: number,
  appointmentId: number,
  patch: BirdEyeCellRaw,
): void {
  queryClient.setQueryData<BirdEyeResponse>(keys.birdEye.live(locationId), (prev) => {
    if (!prev?.operatories) return prev;
    return {
      ...prev,
      operatories: prev.operatories.map((op) =>
        op.cells
          ? {
              ...op,
              cells: op.cells.map((c) =>
                (c.appointment_id ?? c.id) === appointmentId ? applyBirdEyeStatusToCell(c, patch) : c,
              ),
            }
          : op,
      ),
    };
  });
}

/** Both boards read the same appointments, so a status change on one must refresh the other. */
export function invalidateBoards(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['bird-eye'] });
  void queryClient.invalidateQueries({ queryKey: ['calendar'] });
}

/** How long the server's read model needs after a status change before a GET returns the real state. */
export const STATUS_REFETCH_DELAY_MS = 5_000;

/**
 * After a status change, waits for the server to settle and then re-fetches both boards and the
 * appointment, so what stays on screen is what the server really has.
 */
export function refetchAfterStatusChange(queryClient: QueryClient, appointmentId: number): void {
  setTimeout(() => {
    invalidateBoards(queryClient);
    void queryClient.invalidateQueries({ queryKey: keys.appointment.detail(appointmentId) });
    void queryClient.invalidateQueries({ queryKey: keys.appointment.details(appointmentId) });
  }, STATUS_REFETCH_DELAY_MS);
}

function allCells(board: BirdEyeResponse | undefined): BirdEyeCellRaw[] {
  return board?.operatories?.flatMap((op) => op.cells ?? []) ?? [];
}

export function useBirdEye(locationId: number | null | undefined) {
  const queryClient = useQueryClient();
  const queryKey = keys.birdEye.live(locationId ?? 0);
  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const next = await birdEyeApi.getLive(locationId!, { signal });
      if (!next.operatories?.length) return next;
      const prev = allCells(queryClient.getQueryData<BirdEyeResponse>(queryKey));
      return {
        ...next,
        operatories: next.operatories.map((op) =>
          op.cells
            ? { ...op, cells: carryOverRebuilding(op.cells, prev, (c) => c.appointment_id ?? c.id) }
            : op,
        ),
      };
    },
    enabled: locationId != null,
    staleTime: 15_000,
    // Opening the board always shows what the floor looks like now, not a copy up to 15s old.
    refetchOnMount: 'always',
    // Cells still being rebuilt after an update have no start time, so the board drops them.
    refetchInterval: (query) =>
      hasRebuildingCells(allCells(query.state.data)) ? REBUILDING_POLL_MS : 15_000,
  });
}

/** FD82 — the per-patient finished list + honest captured/lost totals. */
export function useBirdEyeFinished(locationId: number | null | undefined) {
  return useQuery({
    queryKey: keys.birdEye.finished(locationId ?? 0),
    queryFn: ({ signal }) => birdEyeApi.getFinished(locationId!, undefined, { signal }),
    enabled: locationId != null,
    staleTime: 30_000,
    refetchOnMount: 'always',
    refetchInterval: 30_000,
  });
}

/**
 * Sets an appointment's visit status. The response carries the updated cell, which
 * is merged into the cached board right away; a refetch 5s later reconciles the rest.
 */
export function useUpdateBirdEyeStatus(locationId: number | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appointmentId,
      status,
    }: {
      appointmentId: number;
      status: BirdEyeVisitStatus;
    }) => birdEyeApi.updateAppointmentStatus(appointmentId, status, locationId!),
    // A poll already in flight would return the pre-move board and yank the card back
    // while the PATCH is still pending.
    onMutate: async () => {
      if (locationId == null) return;
      await queryClient.cancelQueries({ queryKey: keys.birdEye.live(locationId) });
    },
    onSuccess: ({ cell }, { appointmentId }) => {
      if (locationId == null) return;
      patchBirdEyeCell(queryClient, locationId, appointmentId, cell);
      refetchAfterStatusChange(queryClient, appointmentId);
    },
  });
}
